import { computed, readonly, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { NEVER } from 'rxjs';
import { cloneDeep } from 'lodash';

import { ItinerarySearchResults } from '@apps/main/modules/tripPlannerModel/itinerarySearchResults';
import { SmartSearchService } from '@apps/main/modules/search/smartSearchService';
import type {
  PriceCalendarState,
  PriceRibbonMonth,
} from '@apps/main/new-desktop/modules/calendars';
import { getPriceCalendarState } from '@apps/main/new-desktop/core/stores/priceCalendar/priceCalendarStoreLogic';
import type { RecipeMainIngredient } from '@apps/main/modules/recipe/ingredients';
import { isHotelIngredient } from '@apps/main/modules/recipe/ingredients';
import { getImageUrl } from '@apps/main/modules/api/hotels';
import {
  useGlobalCurrencyStore,
  usePaxStore,
  usePriceCalendarStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';
import { useCriteriaStore } from '@apps/main/new-desktop/core/stores/criteria/criteriaStore';
import { adaptRecipeIngredientsToItinerarySearchCriteria } from '@apps/main/modules/criteria/itinerarySearchCriteria/itinerariesCriteria';
import type { DateWithItinerary } from '@apps/main/new-desktop/core/stores/trip/tripStore.model';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { usePreferredTimeStore } from '@apps/main/new-desktop/core/stores/preferredTimeStore';
import { getGuestRating } from '@apps/main/new-desktop/modules/customersRating';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import {
  isAvailableFerryIngredientResponse,
  isAvailableFlightIngredientResponse,
} from '@libs/search-service-smart-search';
import { DateFormat, SemboDate } from '@libs/sembo-dates';
import { isFeatureToggleActive } from '@libs/feature-toggle';
import { assertValue } from '@libs/utils';
import type {
  ContentApiImage,
  HotelBookableOptionWithPriceBreakdown,
  ItinerarySearchCriteria,
  ItinerarySearchUserCriteria,
} from '@libs/trip-planner-interfaces';
import {
  IngredientType,
  // TransportType
} from '@libs/trip-planner-interfaces';

import type {
  PeekPriceBookableTransportCardData,
  PeekPriceModalCalendar,
  PeekPriceModalFerryPlusHotelItinerary,
  PeekPriceModalFlightPlusHotelItinerary,
  PeekPriceModalHotelOnlyItinerary,
  PeekPriceModalItinerary,
  PeekPriceModalUnavailableFerryPlusHotelItinerary,
  PeekPriceModalUnavailableFlightPlusHotelItinerary,
  // PeekPriceModalMultiIngredientItinerary,
  // PeekPriceSmallHotelCardData,
  // PeekPriceUnbookableTransportCardData,
  // PeekPriceMultiIngredientCard,
  PeekPriceModalOldModalItinerary,
} from '../../models';
import {
  PeekPriceModalItineraryType,
  // PeekPriceModalCardType,
} from '../../models';

import {
  doesDateMatchAnyComparisonDate,
  getDateOfCheapestItinerary,
  getHotelSelectionCardSubtitle,
  getMonthStartAndEndDates,
} from './peekPriceStoreLogic';

const SEARCH_SERVICE = new SmartSearchService();

const { t } = i18n.global;

// TODO: This is duplicated
export function getFlightDescriptionFromNumberOfStops(
  numberOfStops: number
): string {
  return numberOfStops === 0
    ? t('planner.direct_flight')
    : t('planner.number_of_stops', [numberOfStops], numberOfStops);
}

export const usePeekPriceStore = defineStore('PeekPriceHotel', () => {
  const preferredTimeStore = usePreferredTimeStore();
  const recipeStore = useRecipeStore();
  const tripStore = useTripStore();
  const priceCalendarStore = usePriceCalendarStore();
  const criteriaStore = useCriteriaStore();
  const globalCurrencyStore = useGlobalCurrencyStore();
  const paxStore = usePaxStore();

  // State
  const itineraryDatesRef = ref<DateWithItinerary[]>([]);
  const selectedMonthISODateRef = ref<string | undefined>(undefined);
  const ingredientIndexRef = ref<number | undefined>(undefined);
  const hotelCodeRef = ref<string | undefined>(undefined);
  const selectedDateRef = ref<Date | undefined>(undefined);

  // Actions
  function loadPrices(ingredientIndex: number, hotelCode: string): void {
    itineraryDatesRef.value = [];
    ingredientIndexRef.value = ingredientIndex;
    hotelCodeRef.value = hotelCode;

    const recipeCopy: RecipeMainIngredient[] = cloneDeep(recipeStore.recipe);
    const ingredient = recipeCopy[ingredientIndex];

    if (!ingredient) {
      return;
    }

    if (!isHotelIngredient(ingredient)) {
      return;
    }

    ingredient.heartedHotels = [{ code: hotelCode }];

    const dates = selectedMonthISODateRef.value
      ? getMonthStartAndEndDates(new Date(selectedMonthISODateRef.value))
      : {
          startDate: preferredTimeStore.startSearchDate,
          endDate: preferredTimeStore.endSearchDate,
          selectedDate:
            selectedDateRef.value ?? preferredTimeStore.preferredDay,
        };

    const userCriteria: ItinerarySearchUserCriteria = {
      ingredients: adaptRecipeIngredientsToItinerarySearchCriteria(recipeCopy),
      pax: paxStore.pax.rooms,
      endSearchDate: dates.endDate,
      startSearchDate: dates.startDate,
      selectedDate: dates.selectedDate,
      sessionId: tripStore.sessionId,
    };
    const criteria: ItinerarySearchCriteria = {
      userCriteria,
      businessCriteria: criteriaStore.businessCriteria,
    };
    const results = new ItinerarySearchResults(SEARCH_SERVICE, criteria, NEVER);
    results.itineraries.subscribe((i) => {
      const itinerary = { date: i.date, itinerary: i };
      const allItineraries = [...itineraryDatesRef.value, itinerary];
      allItineraries.sort((a, b) => a.date.getTime() - b.date.getTime());
      itineraryDatesRef.value = [...allItineraries];
    });
  }

  function selectMonth(date: Date): void {
    const semboDate = new SemboDate(date);
    selectedMonthISODateRef.value = semboDate.format(DateFormat.ISO);
    selectedDateRef.value = undefined;
  }

  function preselectDate(date: Date): void {
    selectedDateRef.value = date;
  }

  function clean(): void {
    itineraryDatesRef.value = [];
    selectedMonthISODateRef.value = undefined;
    ingredientIndexRef.value = undefined;
    hotelCodeRef.value = undefined;
    selectedDateRef.value = undefined;
  }

  function confirm(): void {
    if (ingredientIndexRef.value === undefined || !hotelCodeRef.value) {
      clean();
      return;
    }

    const heartedHotel = { code: hotelCodeRef.value };
    const ingredientId =
      recipeStore.recipe[ingredientIndexRef.value]?.ingredientId;
    assertValue(ingredientId, 'peekPriceStore.ts > clean > ingredientId');
    tripStore.replaceHeartedHotel(heartedHotel, ingredientId);

    if (selectedMonthISODateRef.value && !selectedDateRef.value) {
      tripStore.toggleExpandedMonth(new Date(selectedMonthISODateRef.value));
    }

    if (selectedDateRef.value) {
      preferredTimeStore.setPreferredDay(selectedDateRef.value);
    }

    clean();
  }

  // Getters
  const months = computed<PriceRibbonMonth[]>(() => {
    if (!selectedMonthISODateRef.value) {
      return priceCalendarStore.priceRibbonMonths;
    }

    return priceCalendarStore.priceRibbonMonths.map(
      (month: PriceRibbonMonth) => {
        const isExtended =
          new SemboDate(month.firstDayInMonthISO).format(
            DateFormat.ShortMonthAndYear
          ) ===
          new SemboDate(selectedMonthISODateRef.value).format(
            DateFormat.ShortMonthAndYear
          );

        return { ...month, isExtended };
      }
    );
  });

  const selectedMonth = computed<string>(
    () =>
      selectedMonthISODateRef.value ?? priceCalendarStore.selectedMonthOption.id
  );

  const priceCalendarState = computed<PriceCalendarState>(() =>
    getPriceCalendarState({
      weekdaysFormat: DateFormat.ShortDayName,
      itineraryDates: itineraryDatesRef.value,
      alternativeProductMessage: t('planner.alternative_avail'),
      noAvailabilityMessage: t('planner.no_avail'),
      forceLoader: isFeatureToggleActive('loader'),
      moneyFormanterFn: globalCurrencyStore.formatWithoutCurrency,
      selectedDate: selectedDateRef.value ?? preferredTimeStore.preferredDay,
      suggestedDate: cheapestItineraryDate.value,
      fallbackMonthDate: new Date(selectedMonth.value),
      hideAlternativeProductPrices: true,
    })
  );

  const calendar = computed<PeekPriceModalCalendar>(() => ({
    priceCalendarState: priceCalendarState.value,
    selectedMonth: selectedMonth.value,
    months: months.value,
  }));

  const cheapestItineraryDate = computed<Date | undefined>(() =>
    getDateOfCheapestItinerary(itineraryDatesRef.value)
  );

  const selectedItinerary = computed<DateWithItinerary | undefined>(() => {
    const selectedItinerary = itineraryDatesRef.value.find(
      (itinerary: DateWithItinerary) =>
        doesDateMatchAnyComparisonDate(itinerary.date, [
          selectedDateRef.value,
          preferredTimeStore.preferredDay,
          cheapestItineraryDate.value,
        ])
    );

    return selectedItinerary;
  });

  const itinerary = computed<PeekPriceModalItinerary | undefined>(() => {
    if (ingredientIndexRef.value === undefined || !hotelCodeRef.value) {
      return;
    }

    const ingredientId =
      recipeStore.recipe[ingredientIndexRef.value]?.ingredientId;

    if (!ingredientId) {
      return;
    }

    const hotelStaticInfo = tripStore.getStaticInfo(
      hotelCodeRef.value,
      ingredientId
    );

    if (!hotelStaticInfo) {
      return;
    }

    function calculateDates(): string | undefined {
      const displayDate =
        selectedItinerary.value?.date ?? cheapestItineraryDate.value;

      if (!displayDate) {
        return;
      }

      const startDate = new SemboDate(displayDate);
      const endDate = new SemboDate(displayDate).add({
        days: recipeStore.totalNights,
      });

      const sameMonth =
        startDate.format(DateFormat.MonthFullName) ===
        endDate.format(DateFormat.MonthFullName);

      let dates: string;

      if (sameMonth) {
        dates = `${startDate.format(DateFormat.Day)} - ${endDate.format(
          DateFormat.Day
        )} ${endDate.format(DateFormat.FullMonthAndYear)}`;
      } else {
        dates = `${startDate.format(DateFormat.Day)} ${startDate.format(
          DateFormat.MonthFullName
        )} - ${endDate.format(DateFormat.Day)} ${endDate.format(
          DateFormat.FullMonthAndYear
        )}`;
      }

      return dates;
    }

    const rooms =
      selectedItinerary.value?.itinerary?.hotelOnlyItinerary[0]?.suggested?.rooms.map(
        (room: HotelBookableOptionWithPriceBreakdown) => ({
          description: room.description,
          boardBasisCode: room.boardBasis,
        })
      );

    const imagesLimit = 3;
    const amount = selectedItinerary.value?.itinerary?.totalPrice?.amount ?? 0;
    const price = amount ? globalCurrencyStore.formatMoney(amount) : undefined;
    const dates = calculateDates();

    const hotel = {
      dates,
      imageUrls: hotelStaticInfo.images
        .slice(0, imagesLimit)
        .map((image: ContentApiImage) => getImageUrl(image)),
      nights: `${recipeStore.totalNights} ${t(
        'planner.nights',
        recipeStore.totalNights
      )}`,
      hotelName: hotelStaticInfo.name,
      hotelDestination: getHotelSelectionCardSubtitle(hotelStaticInfo),
      hotelStars: hotelStaticInfo.semboSunRating,
      guestRating: getGuestRating(hotelStaticInfo.guestRatings),
      rooms,
    };

    if (recipeStore.isSingleHotelRecipe) {
      const hotelOnlyItinerary: PeekPriceModalHotelOnlyItinerary = {
        type: PeekPriceModalItineraryType.HotelOnly,
        dates,
        price,
        paxes: paxStore.getPaxesButtonSimpleText,
        hotel,
      };

      return hotelOnlyItinerary;
    }

    if (recipeStore.isFlightPlusHotelRecipe) {
      const placeholder: PeekPriceModalUnavailableFlightPlusHotelItinerary = {
        type: PeekPriceModalItineraryType.UnavailableFlightPlusHotel,
        paxes: paxStore.getPaxesButtonSimpleText,
        dates: undefined,
        price: undefined,
      };

      const departureIngredient =
        selectedItinerary.value?.itinerary?.flightOnlyItinerary[0];
      const arrivalIngredient =
        selectedItinerary.value?.itinerary?.flightOnlyItinerary[1];

      if (
        !departureIngredient ||
        !arrivalIngredient ||
        !isAvailableFlightIngredientResponse(departureIngredient) ||
        !isAvailableFlightIngredientResponse(arrivalIngredient)
      ) {
        return placeholder;
      }

      const departureTime = `${new SemboDate(
        departureIngredient.departureDate
      ).format(DateFormat.Time)} - ${new SemboDate(
        departureIngredient.arrivalDate
      ).format(DateFormat.Time)}`;
      const arrivalTime = `${new SemboDate(
        arrivalIngredient.departureDate
      ).format(DateFormat.Time)} - ${new SemboDate(
        arrivalIngredient.arrivalDate
      ).format(DateFormat.Time)}`;

      const firstTransportDeparturePort = tripStore.getPortStaticInfo(
        departureIngredient.departureIata,
        IngredientType.Flight
      );

      const firstTransportArrivalPort = tripStore.getPortStaticInfo(
        departureIngredient.arrivalIata,
        IngredientType.Flight
      );

      const secondTransportDeparturePort = tripStore.getPortStaticInfo(
        arrivalIngredient.departureIata,
        IngredientType.Flight
      );

      const secondTransportArrivalPort = tripStore.getPortStaticInfo(
        arrivalIngredient.arrivalIata,
        IngredientType.Flight
      );

      if (
        !firstTransportDeparturePort ||
        !firstTransportArrivalPort ||
        !secondTransportDeparturePort ||
        !secondTransportArrivalPort
      ) {
        return placeholder;
      }

      const departureCarrier =
        departureIngredient.platingCarrier ?? departureIngredient.carriers[0];
      const arrivalCarrier =
        arrivalIngredient.platingCarrier ?? arrivalIngredient.carriers[0];

      if (!departureCarrier || !arrivalCarrier) {
        return placeholder;
      }

      const carrierStaticInfoDict = tripStore.getCarriersStaticInfo([
        departureCarrier,
        arrivalCarrier,
      ]);

      const departureTransport: PeekPriceBookableTransportCardData = {
        date: new SemboDate(departureIngredient.departureDate).format(
          DateFormat.DateMonth
        ),
        direction: t('filter.flightFilters.departure'),
        stops: getFlightDescriptionFromNumberOfStops(
          departureIngredient.numberOfStops
        ),
        carrierLogoUrl: carrierStaticInfoDict[departureCarrier]?.logo,
        carrierLogoAltText: t('a11y.flight_carrier_logo'),
        altLogoIcon: 'airplane',
        time: departureTime,
        ports: `${firstTransportDeparturePort.position.name} (${firstTransportDeparturePort.code}) - ${firstTransportArrivalPort.position.name} (${firstTransportArrivalPort.code})`,
      };

      const arrivalTransport: PeekPriceBookableTransportCardData = {
        date: new SemboDate(arrivalIngredient.departureDate).format(
          DateFormat.DateMonth
        ),
        direction: t('filter.flightFilters.arrival'),
        stops: getFlightDescriptionFromNumberOfStops(
          arrivalIngredient.numberOfStops
        ),
        carrierLogoUrl: carrierStaticInfoDict[arrivalCarrier]?.logo,
        carrierLogoAltText: t('a11y.flight_carrier_logo'),
        altLogoIcon: 'airplane',
        time: arrivalTime,
        ports: `${secondTransportDeparturePort.position.name} (${secondTransportDeparturePort.code}) - ${secondTransportArrivalPort.position.name} (${secondTransportArrivalPort.code})`,
      };

      const flightPlusHotelRecipe: PeekPriceModalFlightPlusHotelItinerary = {
        type: PeekPriceModalItineraryType.FlightPlusHotel,
        tripDeparture: new SemboDate(departureIngredient.departureDate).format(
          DateFormat.DateMonth
        ),
        dates,
        price,
        paxes: paxStore.getPaxesButtonSimpleText,
        departureTransport,
        hotel,
        arrivalTransport,
      };

      return flightPlusHotelRecipe;
    }

    if (recipeStore.isFerryPlusHotelRecipe) {
      const placeholder: PeekPriceModalUnavailableFerryPlusHotelItinerary = {
        type: PeekPriceModalItineraryType.UnavailableFerryPlusHotel,
        paxes: paxStore.getPaxesButtonSimpleText,
        dates: undefined,
        price: undefined,
      };

      const departureIngredient =
        selectedItinerary.value?.itinerary?.ferryOnlyItinerary[0];
      const arrivalIngredient =
        selectedItinerary.value?.itinerary?.ferryOnlyItinerary[1];

      if (
        !departureIngredient ||
        !arrivalIngredient ||
        !isAvailableFerryIngredientResponse(departureIngredient) ||
        !isAvailableFerryIngredientResponse(arrivalIngredient)
      ) {
        return placeholder;
      }

      const departureTime = `${new SemboDate(
        departureIngredient.departureDate
      ).format(DateFormat.Time)} - ${new SemboDate(
        departureIngredient.arrivalDate
      ).format(DateFormat.Time)}`;
      const arrivalTime = `${new SemboDate(
        arrivalIngredient.departureDate
      ).format(DateFormat.Time)} - ${new SemboDate(
        arrivalIngredient.arrivalDate
      ).format(DateFormat.Time)}`;

      const firstTransportDeparturePort = tripStore.getPortStaticInfo(
        departureIngredient.departureIata,
        IngredientType.Ferry
      );

      const firstTransportArrivalPort = tripStore.getPortStaticInfo(
        departureIngredient.arrivalIata,
        IngredientType.Ferry
      );

      const secondTransportDeparturePort = tripStore.getPortStaticInfo(
        arrivalIngredient.departureIata,
        IngredientType.Ferry
      );

      const secondTransportArrivalPort = tripStore.getPortStaticInfo(
        arrivalIngredient.arrivalIata,
        IngredientType.Ferry
      );

      if (
        !firstTransportDeparturePort ||
        !firstTransportArrivalPort ||
        !secondTransportDeparturePort ||
        !secondTransportArrivalPort
      ) {
        return placeholder;
      }

      const departureCarrier =
        departureIngredient.platingCarrier ?? departureIngredient.carriers[0];
      const arrivalCarrier =
        arrivalIngredient.platingCarrier ?? arrivalIngredient.carriers[0];

      if (!departureCarrier || !arrivalCarrier) {
        return placeholder;
      }

      const carrierStaticInfoDict = tripStore.getCarriersStaticInfo([
        departureCarrier,
        arrivalCarrier,
      ]);

      const departureTransport: PeekPriceBookableTransportCardData = {
        date: new SemboDate(departureIngredient.departureDate).format(
          DateFormat.DateMonth
        ),
        direction: t('filter.flightFilters.departure'),
        stops: t('planner.direct_flight_short'),
        carrierLogoUrl: carrierStaticInfoDict[departureCarrier]?.logo,
        carrierLogoAltText: t('a11y.ferry_carrier_logo'),
        altLogoIcon: 'ferry',
        time: departureTime,
        ports: `${firstTransportDeparturePort.position.name} (${firstTransportDeparturePort.code}) - ${firstTransportArrivalPort.position.name} (${firstTransportArrivalPort.code})`,
      };

      const arrivalTransport: PeekPriceBookableTransportCardData = {
        date: new SemboDate(arrivalIngredient.departureDate).format(
          DateFormat.DateMonth
        ),
        direction: t('planner.return_ferry'),
        stops: t('planner.direct_flight_short'),
        carrierLogoUrl: carrierStaticInfoDict[arrivalCarrier]?.logo,
        carrierLogoAltText: t('a11y.ferry_carrier_logo'),
        altLogoIcon: 'ferry',
        time: arrivalTime,
        ports: `${secondTransportDeparturePort.position.name} (${secondTransportDeparturePort.code}) - ${secondTransportArrivalPort.position.name} (${secondTransportArrivalPort.code})`,
      };

      const ferryPlusHotelRecipe: PeekPriceModalFerryPlusHotelItinerary = {
        type: PeekPriceModalItineraryType.FerryPlusHotel,
        tripDeparture: new SemboDate(departureIngredient.departureDate).format(
          DateFormat.DateMonth
        ),
        dates,
        price,
        paxes: paxStore.getPaxesButtonSimpleText,
        departureTransport,
        hotel,
        arrivalTransport,
      };

      return ferryPlusHotelRecipe;
    }

    // NOTE: Uncomment this to see how it works
    // const unbookableTransport: PeekPriceUnbookableTransportCardData = {
    //   type: TransportType.Car,
    //   date: '22 July 2025',
    //   travelTime: '3h 34m',
    //   distance: '332km',
    //   icon: 'car',
    //   description: 'Car',
    // };
    // const bookableTransport: PeekPriceBookableTransportCardData = {
    //   date: '29 Jul',
    //   direction: 'Arrival',
    //   stops: 'Direct flight',
    //   carrierLogoUrl:
    //     'https://cdn.sanity.io/files/inntox6v/production/af902f88b4a40eb1399d788b98bb1a46714cf332.svg/TO.svg',
    //   carrierLogoAltText: 'Flight carrier logo',
    //   altLogoIcon: 'airplane',
    //   time: '14:50 - 16:30',
    //   ports: 'Paris (ORY) - Prague (PRG)',
    // };
    // const smallHotel: PeekPriceSmallHotelCardData = {
    //   dates: '24 July 2025',
    //   imageUrl: 'https://images.dog.ceo/breeds/vizsla/n02100583_11473.jpg',
    //   nights: '3 nights',
    //   hotelName: 'Hotel name',
    //   hotelDestination: 'Hotel destination',
    //   rooms: undefined,
    // };
    // const cards: PeekPriceMultiIngredientCard[] = [
    //   {
    //     type: PeekPriceModalCardType.UnbookableTransport,
    //     data: unbookableTransport,
    //   },
    //   {
    //     type: PeekPriceModalCardType.Accommodation,
    //     data: smallHotel,
    //   },
    //   {
    //     type: PeekPriceModalCardType.BookableTransport,
    //     data: bookableTransport,
    //   },
    //   {
    //     type: PeekPriceModalCardType.Accommodation,
    //     data: smallHotel,
    //   },
    //   {
    //     type: PeekPriceModalCardType.BookableTransport,
    //     data: bookableTransport,
    //   },
    //   {
    //     type: PeekPriceModalCardType.UnbookableTransport,
    //     data: unbookableTransport,
    //   },
    //   {
    //     type: PeekPriceModalCardType.Accommodation,
    //     data: smallHotel,
    //   },
    //   {
    //     type: PeekPriceModalCardType.BookableTransport,
    //     data: bookableTransport,
    //   },
    //   {
    //     type: PeekPriceModalCardType.Accommodation,
    //     data: smallHotel,
    //   },
    //   {
    //     type: PeekPriceModalCardType.BookableTransport,
    //     data: bookableTransport,
    //   },
    // ];
    // const peekPriceAccommodationSummary = `${t(
    //   'planner.calendar_shows_date_price'
    // )} Hotel Foo`;
    // const multiIngredient: PeekPriceModalMultiIngredientItinerary = {
    //   type: PeekPriceModalItineraryType.MultiIngredient,
    //   paxes: paxStore.getPaxesButtonSimpleText,
    //   dates,
    //   price,
    //   cards,
    //   peekPriceAccommodationSummary,
    // };
    // return multiIngredient;

    const placeholderItinerary: PeekPriceModalOldModalItinerary = {
      type: PeekPriceModalItineraryType.OldModal,
      hotelName: hotelStaticInfo.name,
    };

    return placeholderItinerary;
  });

  // Watchers
  watch(selectedMonthISODateRef, () => {
    if (
      ingredientIndexRef.value !== undefined &&
      hotelCodeRef.value !== undefined
    ) {
      loadPrices(ingredientIndexRef.value, hotelCodeRef.value);
    }
  });

  return {
    // State
    // * it is considered private for this module
    // * it is exported here to to allow for inspection via pinia dev tools
    itineraryDatesRef: readonly(itineraryDatesRef),
    selectedMonthISODateRef: readonly(selectedMonthISODateRef),
    ingredientIndexRef: readonly(ingredientIndexRef),
    hotelCodeRef: readonly(hotelCodeRef),
    selectedDateRef: readonly(selectedDateRef),

    // Actions
    clean,
    confirm,
    loadPrices,
    preselectDate,
    selectMonth,

    // Getters
    itinerary,
    calendar,
  };
});
